import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  CastFollower,
  type CastKind,
  type FollowerCommand,
  type PlayerState,
} from "../lib/castSync/castFollower";
import { retainServerClock, serverClock } from "../lib/castSync/serverClock";
import { StallDetector } from "../lib/castSync/stallDetector";
import { timelineOf } from "../lib/castSync/timeline";
import logger from "../lib/logger";
import type { StreamSlot } from "../types/stream";

export const DRIFT_LOG_MS = 1000;
/** Android `CastFollowerRunner.TICK_MS`: a stuck player may send no reports, so time is checked here. */
export const STALL_TICK_MS = 250;

/** What a cast player can be told to do. */
export interface CastDriver {
  seek(ms: number): void;
  play(): void;
  pause(): void;
  setRate?(rate: number): void;
}

/** A driver together with the media key it has loaded; it is only used while that key is current. */
export interface KeyedDriver {
  key: string;
  driver: CastDriver;
  /** The media's duration as the player reported it at load; absent while unknown (R22). */
  durationMs?: number;
}

export interface CastFollowerOptions {
  /**
   * Your own cast only (Android `onStall`): the player stuck for 1.5 s after it has played since
   * its last (re)load or seek, with the stuck position. A viewer passes none and detects none.
   */
  onStall?: (positionMs: number) => void;
  /** Every report of the current media (the dock's pause position). */
  onPosition?: (positionMs: number, state: PlayerState) => void;
  /**
   * The follower held the media at its end (R22: the timeline passed the item's end, or the
   * provider looped back to 0). Handle it like the player's own ENDED; a viewer passes none.
   */
  onEnded?: () => void;
}

/**
 * Keeps one cast player on the server timeline (spec 2026-10-12 cast sync §2). `driver` is null
 * until the player takes commands; a driver whose key differs from `mediaKey` (the media changed,
 * the player has not loaded it yet) is treated as null and receives nothing. `report` takes the key
 * of the media the position belongs to; reports for any other media are dropped.
 */
export function useCastFollower(
  slot: StreamSlot,
  kind: CastKind,
  driver: KeyedDriver | null,
  mediaKey: string | null,
  options: CastFollowerOptions = {},
) {
  const follower = useRef<CastFollower | null>(null);
  const followerKey = useRef<string | null>(null);
  const boundDriver = useRef<CastDriver | null>(null);
  const effective = driver && mediaKey != null && driver.key === mediaKey ? driver.driver : null;
  const durationMs = effective ? driver?.durationMs : undefined;
  const driverRef = useRef<CastDriver | null>(effective);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  driverRef.current = effective;
  const onStallRef = useRef(options.onStall);
  onStallRef.current = options.onStall;
  const onPositionRef = useRef(options.onPosition);
  onPositionRef.current = options.onPosition;
  const onEndedRef = useRef(options.onEnded);
  onEndedRef.current = options.onEnded;
  const hasStall = options.onStall != null;
  // Android CastFollowerRunner's stall bookkeeping.
  const stall = useRef(new StallDetector());
  const stallCounting = useRef(false);
  const hasPlayed = useRef(false);

  const resetStall = () => {
    stall.current.reset();
    stallCounting.current = false;
  };
  /** The player (re)loads (new media, new timeline, a fresh bind) or we seeked it: it must report playing again. */
  const mustPlayAgain = () => {
    hasPlayed.current = false;
    resetStall();
  };
  /**
   * Android `stallCounts()`: only while the timeline plays and its start has passed, and only once
   * the player has reported playing since it last (re)loaded or we last seeked it. The detector
   * restarts when counting begins or ends.
   */
  const stallCounts = (f: CastFollower): boolean => {
    const tl = f.timeline;
    const start = tl?.startAt;
    // Held at the end (R22) is like ENDED: the media finished, it is not stuck.
    const counts =
      tl != null && tl.status === "PLAY" && hasPlayed.current && start != null && serverClock.now() >= start && !f.heldAtEnd;
    if (counts !== stallCounting.current) resetStall();
    stallCounting.current = counts;
    return counts;
  };

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  // One follower per media; a new key (or unmount) drops the old follower and its wake timer.
  useLayoutEffect(() => {
    if (mediaKey == null) return;
    follower.current = new CastFollower(kind);
    followerKey.current = mediaKey;
    boundDriver.current = null;
    mustPlayAgain();
    return () => {
      follower.current = null;
      followerKey.current = null;
      boundDriver.current = null;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaKey]);

  useEffect(() => retainServerClock(), []);

  // A driver change or unmount drops the pending wake; the next bind resyncs and re-arms it.
  useEffect(
    () => () => {
      boundDriver.current = null;
      clearTimer();
    },
    [effective, mediaKey],
  );

  const run = useCallback((f: CastFollower, cmds: FollowerCommand[]) => {
    const d = driverRef.current;
    if (!d || follower.current !== f) return;
    for (const c of cmds) {
      if (c.type === "seek") {
        d.seek(c.ms);
        mustPlayAgain(); // our own seek reloads the position: buffering after it is no stall
      }
      else if (c.type === "play") d.play();
      else if (c.type === "pause") d.pause();
      else if (c.type === "rate") d.setRate?.(c.rate);
      else if (c.type === "ended") onEndedRef.current?.();
      else {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          timer.current = null;
          run(f, f.onWake(serverClock.monoNow(), serverClock.now()));
        }, Math.max(0, c.mono - serverClock.monoNow()));
      }
    }
  }, []);

  const t = useMemo(
    () => timelineOf(slot),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slot.playStatus, slot.seekTime, slot.playStartTime, slot.timelineRev],
  );

  useEffect(() => {
    const f = follower.current;
    if (!effective || !f || !t) return;
    const mono = serverClock.monoNow();
    const now = serverClock.now();
    const fresh = boundDriver.current !== effective;
    boundDriver.current = effective;
    f.durationMs = durationMs ?? null; // R22
    const before = f.timeline;
    const cmds = f.onTimeline(t, mono, now);
    // A new timeline loads afresh, a fresh bind is the media (re)loaded: slow loading is no stall.
    if (fresh || f.timeline !== before) mustPlayAgain();
    run(f, fresh && cmds.length === 0 ? f.resync(mono, now) : cmds);
  }, [effective, durationMs, mediaKey, t, run]);

  useEffect(() => {
    if (!effective) return;
    const id = setInterval(() => {
      const f = follower.current;
      if (!f || f.lastDrift == null) return;
      logger.info(
        `[CastSync] slot=${slot.id} kind=${kind} drift=${Math.round(f.lastDrift)} rate=${f.rate} cost=${f.seekCost} clock=${serverClock.hasSample() ? "synced" : "device"}`,
      );
    }, DRIFT_LOG_MS);
    return () => clearInterval(id);
  }, [effective, slot.id, kind]);

  // Becoming (or ceasing to be) your own cast starts with a fresh stuck clock.
  useEffect(() => {
    resetStall();
    if (!hasStall || !effective) return;
    const id = setInterval(() => {
      const f = follower.current;
      const handler = onStallRef.current;
      if (!f || !handler || !driverRef.current || !stallCounts(f)) return;
      const stuck = stall.current.check(serverClock.monoNow());
      if (stuck != null) handler(stuck);
    }, STALL_TICK_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStall, effective, mediaKey]);

  // The server clock estimate moved (first real sample, refresh jump) or the tab woke up (throttled
  // timers): let the follower recompute a pending scheduled start (a playing cast is left to drift correction).
  useEffect(() => {
    const again = () => {
      const f = follower.current;
      if (f && driverRef.current) run(f, f.onClockChange(serverClock.monoNow(), serverClock.now()));
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") again();
    };
    const off = serverClock.subscribe(again);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      off();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [run]);

  const report = useCallback(
    (key: string, positionMs: number, state: PlayerState) => {
      const f = follower.current;
      if (!f || key !== followerKey.current) return;
      onPositionRef.current?.(positionMs, state);
      if (!driverRef.current) return;
      const mono = serverClock.monoNow();
      if (state === "playing") hasPlayed.current = true;
      if (onStallRef.current && stallCounts(f)) stall.current.onReport(positionMs, state, mono);
      run(f, f.onReport(positionMs, state, mono, serverClock.now()));
    },
    [run],
  );

  const resync = useCallback(() => {
    const f = follower.current;
    if (f && driverRef.current) run(f, f.resync(serverClock.monoNow(), serverClock.now()));
  }, [run]);

  return { report, resync };
}
